// @flow
import React, { useContext, useMemo } from 'react'
import { useSideEffects } from './sideEffects'
import type {
	BothFullMissionStates,
	AmbianceSpecialEffectData,
	MissionType,
	Character,
	Map,
} from '@mission.io/mission-toolkit'
import {
	type AnalyticsOverview,
	type SimulationLeaderboard,
	type ClassStudentAnalytics,
	type Award,
	getAnalyticsOverview,
} from '@mission.io/mission-toolkit'
import {
	END_TRIGGER_TYPES as endTriggerTypes,
	AMBIANCE_TYPE_EFFECT,
	MISSION_TYPES,
	typeof DEFAULT_VOLUME_RATIOS as VolumeRatiosObject,
} from '@mission.io/mission-toolkit/constants'
import type {
	EffectId,
	ResolversMap,
	ProviderProps,
	AnalyticsStore,
	PastAnalyticsPerformanceScores,
	CurrentMissionPerformanceScores,
	MissionsApiRequestParams,
} from '@mission.io/mission-player'
import type { ClientQuestion as Question } from '@mission.io/question-toolkit'
import usePlayerReducer, { type PlayerState, type Action } from './reducer'

const defaultResolvers: ResolversMap = {
	videoSourceResolver: (src: string) => {
		return src
	},
	imageSourceResolver: (src: string) => {
		return src
	},
	navigationSourceResolver: (src: string) => {
		return src
	},
	vocalTrackSourceResolver: (src: string) => {
		return src
	},
	songSourceResolver: (src: string) => {
		return src
	},
	soundEffectSourceResolver: (src: string) => {
		return src
	},
}

type Context = {
	onEndCallbacks: {
		ambianceEffect: (AmbianceSpecialEffectData, () => void) => void,
		specialEffect: string => void,
		vocalTrack: string => void,
		videoOverlay: string => void,
		videoScreen: string => void,
		culminatingMoment: string => void,
		results: () => void,
	},
	missionState: ?BothFullMissionStates,
	missionType: MissionType,
	missionCode: string,
	staticQuestions: ?(Question[]),
	maps: Map[],
	silent: boolean,
	showErrors: boolean,
	controlledSoundEffects: { [EffectId]: { isPlaying: boolean } },
	getCharacter: string => ?Character,
	getAction: string => ?mixed,
	analytics: ?AnalyticsStore,
	showAccessibility: boolean,
	audioOutputId?: string,
	volumes: ?VolumeRatiosObject,
	resolvers: ResolversMap,
	hideNavigation: boolean,
	debug?: boolean,
	domainForControls: string,
	canvasWebsocketUrl: ?string,
	sendWebsocketMessage: (message: { type: string, payload?: mixed }) => mixed,
	sendMissionsApiRequest: (
		path: string,
		options: MissionsApiRequestParams
	) => Promise<?{ data: any }>,
	allowControl: boolean,
	hasMissionStarted?: boolean,
}

const PlayerContext = React.createContext<?Context>()

/** If an ambiance effect end function is not provided, use this instead, it will replay the ambiance sound if necessary */
function defaultAmbianceEffectEnd(
	effect: AmbianceSpecialEffectData,
	replaySound: () => void
) {
	if (
		effect.endTrigger === endTriggerTypes.AUDIO &&
		effect.type === AMBIANCE_TYPE_EFFECT
	) {
		setTimeout(replaySound, effect.flashAlternative?.endTriggerDelay || 0)
	}
}

/**
 * The no-op is here so that we don't need to conditionally check for a function that doesn't exist every time we call it.
 * Provider context uses it for several optional functions that may or may not
 * be provided by the calling components.
 */
function noop() {
	// DO NOTHING
}
function noopPromise() {
	return Promise.resolve()
}

/**
 The Provider required for the player to interact properly with mission state. 
 */
export default function PlayerProvider({
	missionType,
	missionState,
	missionCode,
	getAction,
	getCharacter,
	staticQuestions,
	maps,
	showAccessibility,
	analytics,
	silent,
	controlledSoundEffects,
	showErrors,
	onVocalTrackEnd,
	onVideoOverlayEnd,
	onVideoScreenEnd,
	onCulminatingMomentEnd,
	onSpecialEffectEnd,
	onAmbianceEffectEnd,
	onResultsEnd,
	audioOutputId,
	volumes,
	hideNavigation,
	resolvers,
	hasMissionStarted,
	children,
	...props
}: {
	...ProviderProps,
	children: React$Node,
}): React$Node {
	const onEndCallbacks = useMemo(
		() => ({
			specialEffect: onSpecialEffectEnd || noop,
			ambianceEffect: onAmbianceEffectEnd || defaultAmbianceEffectEnd,
			vocalTrack: onVocalTrackEnd || noop,
			videoOverlay: onVideoOverlayEnd || noop,
			videoScreen: onVideoScreenEnd || noop,
			culminatingMoment: onCulminatingMomentEnd || noop,
			results: onResultsEnd || noop,
		}),
		[
			onVocalTrackEnd,
			onVideoOverlayEnd,
			onVideoScreenEnd,
			onCulminatingMomentEnd,
			onResultsEnd,
			onSpecialEffectEnd,
			onAmbianceEffectEnd,
		]
	)
	const providerValue = useMemo(() => {
		return {
			controlledSoundEffects: controlledSoundEffects || {},
			missionState: missionState,
			onEndCallbacks,
			missionType: missionType,
			missionCode: missionCode,
			staticQuestions: staticQuestions,
			maps: maps,
			showErrors: showErrors || false,
			silent: silent || false,
			getAction: getAction,
			getCharacter: getCharacter,
			analytics: analytics,
			showAccessibility: showAccessibility,
			audioOutputId: audioOutputId,
			volumes: volumes,
			resolvers: resolvers || defaultResolvers,
			hideNavigation: hideNavigation || false,
			debug: props.debug,
			domainForControls: props.domainForControls,
			sendWebsocketMessage: props.sendWebsocketMessage || noop,
			sendMissionsApiRequest: props.sendMissionsApiRequest || noopPromise,
			allowControl: props.allowControl || false,
			canvasWebsocketUrl: props.canvasWebsocketUrl,
			hasMissionStarted,
		}
	}, [
		controlledSoundEffects,
		missionState,
		onEndCallbacks,
		missionType,
		missionCode,
		staticQuestions,
		maps,
		showErrors,
		silent,
		getAction,
		getCharacter,
		analytics,
		showAccessibility,
		audioOutputId,
		volumes,
		resolvers,
		hideNavigation,
		hasMissionStarted,
		props.debug,
		props.domainForControls,
		props.sendWebsocketMessage,
		props.canvasWebsocketUrl,
		props.allowControl,
		props.sendMissionsApiRequest,
	])
	return (
		<PlayerContext.Provider value={providerValue}>
			<Store isSilent={silent || false}>
				<SideEffects />
				{children}
			</Store>
		</PlayerContext.Provider>
	)
}
const StoreContext = React.createContext<?[PlayerState, (Action) => void]>()

function Store({
	children,
	isSilent,
}: {
	children: React$Node,
	isSilent: boolean,
}) {
	const value = usePlayerReducer(isSilent)
	return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>
}

/**
 * Side effects must be rendered as a child of the provider so that it can access values from the Player context
 */
function SideEffects() {
	useSideEffects()
	return null
}

/**
 * returns the current player context
 */
function usePlayerContext(): Context {
	const context = useContext(PlayerContext)
	if (!context) {
		throw new Error('Player context must be used from within a PlayerProvider')
	}
	return context
}

/**
 * returns current mission state (could be standard or junior)
 */
export function useMissionState(): ?BothFullMissionStates {
	const { missionState } = usePlayerContext()
	return missionState
}

/**
 * returns the mission type ('K-3' | '4+')
 */
export function useMissionType(): MissionType {
	const { missionType } = usePlayerContext()
	return missionType
}

/**
 * returns the mission code
 */
export function useMissionCode(): ?string {
	const { missionCode } = usePlayerContext()
	return missionCode
}

/**
 * returns the websocket url used to access the creative canvas documents.
 */
export function useCanvasWebsocketUrl(): ?string {
	const { canvasWebsocketUrl } = usePlayerContext()
	return canvasWebsocketUrl
}

export function useMissionsApiRequest(): (
	path: string,
	options: MissionsApiRequestParams
) => Promise<?{ data: any }> {
	const { sendMissionsApiRequest } = usePlayerContext()
	return sendMissionsApiRequest
}

/**
 * returns true if the mission is standard (junior plus) mission
 */
export function useIsJuniorPlus(): boolean {
	const missionType = useMissionType()
	if (missionType === '4+') {
		return true
	}
	return false
}

/**
 * gets all static questions
 */
export function useStaticQuestions(): ?(Question[]) {
	const { staticQuestions } = usePlayerContext()
	return staticQuestions
}

/**
 * gets if a mission has already been started. Defaults to false.
 */
export function useHasMissionStarted(): boolean {
	const { hasMissionStarted } = usePlayerContext()
	return hasMissionStarted ?? false
}

/**
 * gets all maps
 */
export function useMaps(): Map[] {
	return usePlayerContext().maps
}

/**
 * returns true if the mission is junior
 */
export function useIsJunior(): boolean {
	const missionType = useMissionType()
	if (missionType === MISSION_TYPES.K_THROUGH_3) {
		return true
	}
	return false
}

/**
 * gets a function to call when a special effect ends
 */
export function useOnSpecialEffectEnd(): (specialEffectId: string) => void {
	const {
		onEndCallbacks: { specialEffect },
	} = usePlayerContext()
	return specialEffect
}

/**
 * gets a function to call when an ambiance effect ends
 */
export function useOnAmbianceEffectEnd(): (
	AmbianceSpecialEffectData,
	() => void
) => void {
	const {
		onEndCallbacks: { ambianceEffect },
	} = usePlayerContext()
	return ambianceEffect
}

/**
 * gets a function to call when a vocal track ends
 */
export function useOnVocalTrackEnd(): (vocalTrackId: string) => void {
	const {
		onEndCallbacks: { vocalTrack },
	} = usePlayerContext()
	return vocalTrack
}

/**
 * gets a function to call when a video overlay ends
 */
export function useOnVideoOverlayEnd(): (overlayId: string) => void {
	const {
		onEndCallbacks: { videoOverlay },
	} = usePlayerContext()
	return videoOverlay
}

/**
 * gets a function to call when a video screen ends
 */
export function useOnVideoScreenEnd(): (videoScreenId: string) => void {
	const {
		onEndCallbacks: { videoScreen },
	} = usePlayerContext()
	return videoScreen
}

/**
 * gets a function to call when a culminating moment ends
 */
export function useOnCulminatingMomentEnd(): (
	culminatingMomentId: string
) => void {
	const {
		onEndCallbacks: { culminatingMoment },
	} = usePlayerContext()
	return culminatingMoment
}

/**
 * gets a function to call when the results from the end of mission sequence is over
 */
export function useOnResultsEnd(): () => void {
	const {
		onEndCallbacks: { results },
	} = usePlayerContext()
	return results
}

/**
 * whether or not the player should be silent
 */
export function useIsSilent(): boolean {
	const { silent } = usePlayerContext()
	return silent
}

/**
 * uses the audio output id
 */
export function useAudioOutputId(): ?string {
	const { audioOutputId } = usePlayerContext()
	return audioOutputId
}

/**
 * whether or not we can allow the player to display errors to the user
 */
export function useShowErrors(): boolean {
	const { showErrors } = usePlayerContext()
	return showErrors
}

/**
 * whether or not the player should show accessibility
 */
export function useShowAccessibility(): boolean {
	const { showAccessibility } = usePlayerContext()
	return showAccessibility
}

/**
 * accesses the overview of the analytics from context
 */
export function useAnalyticsOverview(): {
	analyticsReceived: boolean,
	overView: ?AnalyticsOverview,
} {
	const { analytics } = usePlayerContext()
	return useMemo(() => {
		if (!analytics) {
			return {
				analyticsReceived: false,
				overView: null,
			}
		}
		return {
			analyticsReceived: true,
			overView: getAnalyticsOverview(analytics.analytics),
		}
	}, [analytics])
}

/**
 * useAnalyticsClassAwards - get the awards from the analytics
 *
 * @returns ?Array<Awards>
 */
export function useAnalyticsClassAwards(): ?Array<Award> {
	const { analytics } = usePlayerContext()
	return analytics?.analytics?.awards
}

/**
 * useAnalyticsAverageClassHealth - get the average class health from the analytics
 *
 * @returns ?number
 */
export function useAnalyticsAverageClassHealth(): ?number {
	const { analytics } = usePlayerContext()
	return analytics?.analytics?.averageClassHealth
}

/**
 * useCurrentAnalytics - get the current analytics in the analytics store
 *
 * @returns ClassStudentAnalytics
 */
export function useCurrentAnalytics(): ?ClassStudentAnalytics {
	return usePlayerContext()?.analytics?.analytics
}

/**
 * useAnalyticsPerformance - access the classes current and previous missions performance statistics
 *
 * @return {
 *	pastAnalyticsPerformanceScores: ?Array<PastAnalyticsPerformanceScores>, - the performance of the past missions for the current class
 *	currentMissionPerformanceScores: ?CurrentMissionPerformanceScores, - the performance of the current mission
 * }
 */
export function useAnalyticsPerformance(): {
	pastAnalyticsPerformanceScores: ?Array<PastAnalyticsPerformanceScores>,
	currentMissionPerformanceScores: ?CurrentMissionPerformanceScores,
} {
	const { analytics } = usePlayerContext()
	return useMemo(() => {
		if (!analytics) {
			return {
				pastAnalyticsPerformanceScores: null,
				currentMissionPerformanceScores: null,
			}
		}
		return {
			pastAnalyticsPerformanceScores: analytics.pastAnalyticsPerformanceScores,
			currentMissionPerformanceScores:
				analytics.currentMissionPerformanceScores,
		}
	}, [analytics])
}

/**
 * accesses the simulation leaderboard from the context
 */
export function useLeaderboard(): {
	leaderboardReceived: boolean,
	leaderboard: ?SimulationLeaderboard,
} {
	const { analytics } = usePlayerContext()
	return useMemo(() => {
		if (!analytics) {
			return {
				leaderboardReceived: false,
				leaderboard: null,
			}
		}
		return {
			leaderboardReceived: true,
			leaderboard: analytics.leaderboard,
		}
	}, [analytics])
}

/**
 * Returns a function which is able to fetch an action given an action id
 */
export function useGetAction(): string => ?mixed {
	const { getAction } = usePlayerContext()
	return getAction
}

/**
 * Finds an action given an actionId and returns it.
 */
export function useAction(actionId: ?string): ?mixed {
	const { getAction } = usePlayerContext()
	return actionId ? getAction(actionId) : null
}

/**
 * Finds a character given a character id and returns it.
 */
export function useCharacter(characterId: ?string): ?Character {
	const { getCharacter } = usePlayerContext()
	return characterId ? getCharacter(characterId) : null
}

/**
 * Returns a local dispatch function.
 */
export function useLocalDispatch(): Action => void {
	const context = useContext(StoreContext)
	if (!context) {
		throw new Error(
			'Store context must be used from within the Store Provider in the player'
		)
	}
	return context[1]
}

/**
 * Returns the local state
 */
export function useLocalState(): PlayerState {
	const context = useContext(StoreContext)
	if (!context) {
		throw new Error(
			'Store context must be used from within the Store Provider in the player'
		)
	}
	return context[0]
}

export function useControlledSoundEffects(): {
	[EffectId]: { isPlaying: boolean },
} {
	const { controlledSoundEffects } = usePlayerContext()
	return controlledSoundEffects
}

export function useDomainForControls(): string {
	const { domainForControls } = usePlayerContext()
	return domainForControls
}

export function useVolumes(): ?VolumeRatiosObject {
	const { volumes } = usePlayerContext()
	return volumes
}

export function useResolvers(): ResolversMap {
	const { resolvers } = usePlayerContext()
	return resolvers
}

export function useHideNavigation(): boolean {
	const { hideNavigation } = usePlayerContext()
	return hideNavigation
}

export function useIsDebugging(): ?boolean {
	return usePlayerContext().debug
}

export type WebsocketMessage = {
	type: string,
	payload?: mixed,
}

/**
 * Get the `sendWebsocketMessage` function from the player context
 */
export function useSendWebsocketMessage(): (
	message: WebsocketMessage
) => mixed {
	return usePlayerContext().sendWebsocketMessage
}

/**
 * Whether the player is set up to control the mission
 */
export function useCanControlMission(): boolean {
	return usePlayerContext().allowControl
}
